import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import "./Styles/JobSign-Up.css";
import { useRegister } from "../hooks/useAuth";

const SignUp = () => {
  const { register, loading } = useRegister();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    const regexUppercase = /[A-Z]/;
    const regexLowercase = /[a-z]/;
    const regexNumber = /[0-9]/;
    const regexSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
    if (!regexUppercase.test(password))
      return "Password must contain at least one uppercase letter.";
    if (!regexLowercase.test(password))
      return "Password must contain at least one lowercase letter.";
    if (!regexNumber.test(password))
      return "Password must contain at least one number.";
    if (!regexSpecialChar.test(password))
      return "Password must contain at least one special character.";
    return null; // Return null if all validations pass
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Validate password on change
    if (name === "password") {
      const passwordValidationError = validatePassword(value);
      setPasswordError(passwordValidationError || "");

      // Also check confirm password if it exists
      if (form.confirmPassword && value !== form.confirmPassword) {
        setConfirmPasswordError("Passwords do not match.");
      } else if (form.confirmPassword && value === form.confirmPassword) {
        setConfirmPasswordError("");
      }
    }

    // Validate confirm password on change
    if (name === "confirmPassword") {
      if (value !== form.password) {
        setConfirmPasswordError("Passwords do not match.");
      } else {
        setConfirmPasswordError("");
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const validateForm = () => {
    const { companyName, email, password, confirmPassword } = form;
    console.log("Form Inputs", form);
    console.log("Selected File:", selectedFile);
    console.log("Selected File type:", typeof selectedFile);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!companyName.trim()) {
      message.error("Company name is required");
      return false;
    }

    if (!email.trim()) {
      message.error("Email is required");
      return false;
    } else if (!emailRegex.test(email)) {
      message.error("Invalid email format");
      return false;
    }

    if (!password.trim()) {
      message.error("Password is required");
      return false;
    }

    // Check password validation
    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      message.error(passwordValidationError);
      return false;
    }

    if (password !== confirmPassword) {
      message.error("Passwords do not match");
      return false;
    }

    if (!selectedFile || selectedFile === null) {
      message.error("Company image is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure the form is validated
    if (!validateForm()) return;

    // Log to ensure data is being captured properly
    console.log("Submitting form data:", form);

    // Prepare form data for submission
    const formData = new FormData();
    formData.append("company_name", form.companyName);
    formData.append("email", form.email);
    formData.append("password", form.password);

    // Only append file if a new one was selected
    if (selectedFile) {
      formData.append("company_logo", selectedFile);
    }

    try {
      // Log the FormData before making the API call
      console.log("FormData to submit:", formData);

      await register(formData); // Assuming `register` is an API call
      message.success("Account created successfully!");
      navigate("/jobs/job-auth");
      // Reset form
      setForm({
        companyName: "",
        email: "",
        password: "",
        confirmPassword: "", // Ensure confirmPassword is also reset
      });
      setSelectedFile(null);
      setPasswordError("");
      setConfirmPasswordError("");
    } catch (error) {
      message.error(
        "Couldn't create account: " + error?.response?.data?.message
      );
      // console.error("create Account error:", error.data.message);
    }
  };

  return (
    <div className="job-signup-container">
      <div className="job-signup-box">
        <p className="job-headtext">Sign Up</p>
        {/* <p className="job-subtext">
          Input the required credentials to create an account to have access to
          your jobs admin dashboard
        </p> */}

        <form onSubmit={handleSubmit}>
          <label>Company Name</label>
          <input
            type="text"
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
            required
          />

          <label>Email Address</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
          />

          <label>Create Password</label>
          <div className="job-password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={form.password}
              onChange={handleChange}
              required
            />
            <span
              className="job-toggle-icon"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </span>
          </div>
          {passwordError && (
            <div
              className="job-error-message"
              style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
            >
              {passwordError}
            </div>
          )}

          <label>Confirm Password</label>
          <div className="job-password-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
            />
            <span
              className="job-toggle-icon"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </span>
          </div>
          {confirmPasswordError && (
            <div
              className="job-error-message"
              style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
            >
              {confirmPasswordError}
            </div>
          )}

          <label>Company Logo</label>
          <div>
            <input type="file" onChange={handleFileChange} accept="image/*" />
            {selectedFile && (
              <p className="file-info">Selected: {selectedFile.name}</p>
            )}
          </div>

          <button type="submit" className="job-signup-btn" disabled={loading}>
            {loading ? "Signing up..." : "Sign Up"}
          </button>
        </form>

        <p className="job-signup-link">
          Already have an account?{" "}
          <Link to="/jobs/job-auth" className="job-signup-link-2">
            Log In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignUp;
